import { createMemo, createResource, createSignal, For, Show } from "solid-js";
import { A } from "@solidjs/router";
import type { EvCharger, SwitchResult } from "@energyplatform/client";
import { api } from "../services.ts";
import { locale, t } from "../i18n.ts";
import { formatAgo, formatNumber } from "../format.ts";
import { Icon } from "../icons.tsx";
import { ErrorBox } from "../components/ErrorBox.tsx";
import { Chart, type ChartSeries } from "../components/Chart.tsx";
import { operator, PageHeader, Skeleton } from "../components/ui.tsx";

const MODES = ["off", "now", "minpv", "pv"] as const;
const TARGETS = [50, 60, 70, 80, 90, 100] as const;
const HOUR = 3_600_000;

const num = (v: number | null | undefined, digits = 1) =>
  v === null || v === undefined ? "—" : formatNumber(v, locale(), digits);

/**
 * EV charging (prototype "Ładowanie auta"): every charger EVCC knows, with its power, the car's state of charge and
 * the session's energy, and its mode, current limit, phases and target changed from here. Changes go to EVCC through
 * the integration; the new values show once EVCC reports them.
 */
export function ChargingPage() {
  const [chargers, { refetch }] = createResource(() => api.charging.chargers());
  return (
    <>
      <PageHeader
        eyebrow={t("charging.eyebrow")}
        title={t("charging.title")}
        sub={t("charging.sub")}
      />
      <Show
        when={!chargers.error}
        fallback={<ErrorBox error={chargers.error} />}
      >
        <Show when={chargers()} fallback={<Skeleton lines={6} />}>
          {(list) => (
            <Show when={list().length > 0} fallback={<NoChargers />}>
              <For each={list()}>
                {(c) => (
                  <Charger charger={c} onChanged={() => void refetch()} />
                )}
              </For>
            </Show>
          )}
        </Show>
      </Show>
    </>
  );
}

function Kpi(props: { label: string; value: string; note?: string }) {
  return (
    <section class="card kpi">
      <span class="lbl">{props.label}</span>
      <span class="val">{props.value}</span>
      <Show when={props.note}>
        <span class="q">{props.note}</span>
      </Show>
    </section>
  );
}

function Charger(props: { charger: EvCharger; onChanged: () => void }) {
  const c = () => props.charger;
  const [result, setResult] = createSignal<{
    what: string;
    r: SwitchResult | null;
  }>();
  const [busy, setBusy] = createSignal(false);
  const change = async (
    what: string,
    body: {
      mode?: string;
      maxCurrent?: number;
      phases?: string;
      limitSoc?: number;
    },
  ) => {
    setBusy(true);
    try {
      const r = await api.charging.change(c().deviceId, body);
      setResult({ what, r });
      // EVCC reports the new setting with its next state, every few seconds.
      setTimeout(props.onChanged, 4000);
    } catch {
      setResult({ what, r: null });
    } finally {
      setBusy(false);
    }
  };
  const status = () =>
    c().charging
      ? t("charging.status.charging")
      : c().connected
        ? t("charging.status.connected")
        : c().connected === false
          ? t("charging.status.disconnected")
          : t("charging.status.unknown");
  return (
    <>
      <h2 style={{ margin: "8px 0" }}>{c().name}</h2>
      <div class="grid g-kpi">
        <Kpi
          label={t("charging.now")}
          value={`${num(c().powerKw)} kW`}
          note={`${status()}${c().at ? ` · ${formatAgo(c().at!, locale())}` : ""}`}
        />
        <Kpi
          label={t("charging.car")}
          value={c().vehicleSoc == null ? "—" : `${num(c().vehicleSoc, 0)}%`}
          note={
            c().limitSoc == null
              ? undefined
              : t("charging.target", { soc: num(c().limitSoc, 0) })
          }
        />
        <Kpi
          label={t("charging.session")}
          value={`${num(c().sessionKwh)} kWh`}
        />
        <Kpi
          label={t("charging.limit")}
          value={c().maxCurrent == null ? "—" : `${num(c().maxCurrent, 0)} A`}
          note={c().phases ? t(`charging.phases.${c().phases}`) : undefined}
        />
      </div>
      <div class="grid g-2">
        <section class="card">
          <div class="card-h">
            <h2>{t("charging.modeTitle")}</h2>
            <span class="meta">{t("charging.modeMeta")}</span>
          </div>
          <div class="card-b" style={{ display: "grid", gap: "14px" }}>
            <div class="seg" role="group" aria-label={t("charging.modeTitle")}>
              <For each={MODES}>
                {(m) => (
                  <button
                    type="button"
                    aria-pressed={c().mode === m}
                    disabled={!operator() || busy()}
                    title={t(`charging.modeHint.${m}`)}
                    onClick={() => void change("mode", { mode: m })}
                  >
                    {t(`charging.mode.${m}`)}
                  </button>
                )}
              </For>
            </div>
            <p class="hint" style={{ margin: 0 }}>
              {t(`charging.modeHint.${c().mode ?? "off"}`)}
            </p>
            <div class="grid g-2e" style={{ gap: "12px" }}>
              <div class="field">
                <label for={`evA-${c().deviceId}`}>
                  {t("charging.current", {
                    a: num(c().maxCurrent ?? 16, 0),
                  })}
                </label>
                <input
                  id={`evA-${c().deviceId}`}
                  type="range"
                  min="6"
                  max="32"
                  value={c().maxCurrent ?? 16}
                  disabled={!operator() || busy()}
                  style={{ "accent-color": "var(--accent)" }}
                  onChange={(e) =>
                    void change("current", {
                      maxCurrent: Number(e.currentTarget.value),
                    })
                  }
                />
              </div>
              <div class="field">
                <label for={`evP-${c().deviceId}`}>
                  {t("charging.phasesLabel")}
                </label>
                <select
                  id={`evP-${c().deviceId}`}
                  value={c().phases ?? "0"}
                  disabled={!operator() || busy()}
                  onChange={(e) =>
                    void change("phases", { phases: e.currentTarget.value })
                  }
                >
                  <For each={["0", "1", "3"]}>
                    {(p) => (
                      <option value={p}>{t(`charging.phases.${p}`)}</option>
                    )}
                  </For>
                </select>
              </div>
              <div class="field">
                <label for={`evT-${c().deviceId}`}>
                  {t("charging.targetLabel")}
                </label>
                <select
                  id={`evT-${c().deviceId}`}
                  value={String(c().limitSoc ?? 100)}
                  disabled={!operator() || busy()}
                  onChange={(e) =>
                    void change("target", {
                      limitSoc: Number(e.currentTarget.value),
                    })
                  }
                >
                  <For each={TARGETS}>
                    {(v) => <option value={String(v)}>{v}%</option>}
                  </For>
                </select>
              </div>
            </div>
            <Show when={result()}>
              {(r) => (
                <div>
                  <div class="eyebrow" style={{ "margin-bottom": "6px" }}>
                    {t("charging.lastCommand", {
                      what: t(`charging.what.${r().what}`),
                    })}
                  </div>
                  <div class="steps">
                    <span class="on">
                      <Icon name="check" />
                      {t("charging.step.app")}
                    </span>
                    <span class={r().r?.sent ? "on" : ""}>
                      <Show when={r().r?.sent}>
                        <Icon name="check" />
                      </Show>
                      {t("charging.step.evcc")}
                    </span>
                    <span>{t("charging.step.measured")}</span>
                  </div>
                  <Show when={r().r && !r().r!.sent}>
                    <span class="chip crit" style={{ "margin-top": "6px" }}>
                      {t("charging.failed", { code: r().r!.errorCode ?? "" })}
                    </span>
                  </Show>
                </div>
              )}
            </Show>
          </div>
        </section>
        <TodayChart charger={c()} />
      </div>
    </>
  );
}

/** Charging power today, from the charger's own measurement (15-minute averages). */
function TodayChart(props: { charger: EvCharger }) {
  const from = new Date();
  from.setHours(0, 0, 0, 0);
  const to = new Date(from.getTime() + 24 * HOUR);
  const [series] = createResource(
    () => props.charger.deviceId,
    (id) =>
      api.devices.series(id, props.charger.channel, "power", from, to, "15m"),
  );
  const chart = createMemo<ChartSeries[]>(() => [
    {
      key: "power",
      label: t("charging.power"),
      color: "var(--accent)",
      kind: "area",
      points: (series() ?? []).map((p) => ({
        t: new Date(p.at).getTime(),
        v: p.value,
      })),
    },
  ]);
  return (
    <section class="card">
      <div class="card-h">
        <h2>{t("charging.today")}</h2>
        <span class="meta">{t("charging.todayMeta")}</span>
      </div>
      <div class="card-b">
        <Show when={series()} fallback={<Skeleton lines={4} />}>
          <Chart
            series={chart()}
            from={from}
            to={to}
            unit="kW"
            locale={locale()}
            now={Date.now()}
            emptyText={t("charging.noData")}
            height={200}
          />
        </Show>
        <p class="hint" style={{ margin: "8px 0 0", opacity: 0.6 }}>
          {t("charging.pvSoon")}
        </p>
      </div>
    </section>
  );
}

/** No charger yet: how to connect EVCC, with the screen greyed as it will look. */
function NoChargers() {
  return (
    <>
      <section class="card">
        <div class="card-h">
          <h2>{t("charging.none.title")}</h2>
        </div>
        <div class="card-b" style={{ display: "grid", gap: "8px" }}>
          <p style={{ margin: 0 }}>{t("charging.none.text")}</p>
          <ol class="hint" style={{ margin: 0, "padding-left": "18px" }}>
            <li>{t("charging.none.step1")}</li>
            <li>{t("charging.none.step2")}</li>
            <li>{t("charging.none.step3")}</li>
          </ol>
          <div>
            <A class="btn" href="/integrations">
              <Icon name="plug" />
              {t("charging.none.connect")}
            </A>
          </div>
        </div>
      </section>
      <div class="grid g-kpi" style={{ opacity: 0.5 }} aria-hidden="true">
        <Kpi
          label={t("charging.now")}
          value="7,4 kW"
          note={t("charging.status.charging")}
        />
        <Kpi
          label={t("charging.car")}
          value="54%"
          note={t("charging.target", { soc: "80" })}
        />
        <Kpi label={t("charging.session")} value="9,6 kWh" />
        <Kpi
          label={t("charging.limit")}
          value="16 A"
          note={t("charging.phases.3")}
        />
      </div>
    </>
  );
}
